'use client';

import { c as _c } from "react/compiler-runtime";
import { type FactiiiTag } from '@shared/all/domains/factiiis';
import { convertToFactiiiTag } from '@shared/utils/helpers/formatNumbers';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useInstanceSpaceSlug } from '../../hooks/useInstanceSpaceSlug';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import AddFactiiiModal from '../spaces/AddFactiiiModal';
import Input from '../UI/Input';
import { FactiiiSearchResult } from './FactiiiSearchResult';
type Props = {
  myPostFactiiiCounts: FactiiiTag[];
  handleClick: (tag: FactiiiTag) => void;
  selectedTag?: FactiiiTag;
  /**
   * Limit results to one space. Set from the route by the caller, so opening
   * this on a board searches that board first — the factiiis you want are
   * almost always the ones already in use here. Undefined searches everywhere.
   */
  spaceSlug?: string;
  /** The award card for a tag that is an award factiii, so the list labels it
   *  and shows its cost. Undefined for any other tag. */
  awardTag?: (tag: FactiiiTag) => FactiiiTag | undefined;
  /** Factiiis to leave out of the list: the awards already staged. */
  hiddenIds?: Set<number>;
};
export const FactiiiSearch = props => {
  const $ = _c(49);
  const [factiiiQuery, setFactiiiQuery] = useState("");
  const [addFactiiiModalOpen, setAddFactiiiModalOpen] = useState(false);
  const {
    currentUser
  } = useUser();
  const preferSpaceSlug = useInstanceSpaceSlug();
  let t0;
  if ($[0] !== factiiiQuery || $[1] !== preferSpaceSlug || $[2] !== props.spaceSlug) {
    t0 = ["myFactiiiQuery", factiiiQuery, props.spaceSlug, preferSpaceSlug];
    $[0] = factiiiQuery;
    $[1] = preferSpaceSlug;
    $[2] = props.spaceSlug;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== factiiiQuery || $[5] !== preferSpaceSlug || $[6] !== props.myPostFactiiiCounts || $[7] !== props.selectedTag || $[8] !== props.spaceSlug) {
    t1 = () => unwrapFromResult(client.search.factiiis.query({
      query: factiiiQuery,
      spaceSlug: props.spaceSlug,
      preferSpaceSlug,
      ignoredIds: [...(props.selectedTag?.factiiiId ? [props.selectedTag.factiiiId] : []), ...props.myPostFactiiiCounts.map(_temp)]
    }));
    $[4] = factiiiQuery;
    $[5] = preferSpaceSlug;
    $[6] = props.myPostFactiiiCounts;
    $[7] = props.selectedTag;
    $[8] = props.spaceSlug;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== t0 || $[11] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      refetchOnWindowFocus: false,
      placeholderData: _temp2,
      staleTime: 30000
    };
    $[10] = t0;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const {
    data: searchData,
    refetch
  } = useQuery(t2);
  let t3;
  if ($[13] !== props.myPostFactiiiCounts) {
    t3 = new Set(props.myPostFactiiiCounts.map(_temp3));
    $[13] = props.myPostFactiiiCounts;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const selectedIds = t3;
  let searchTags;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[15] !== factiiiQuery || $[16] !== props || $[17] !== searchData?.factiiis || $[18] !== selectedIds) {
    let t8;
    if ($[24] !== props.hiddenIds || $[25] !== selectedIds) {
      t8 = tag_0 => !selectedIds.has(tag_0.id) && !props.hiddenIds?.has(tag_0.id);
      $[24] = props.hiddenIds;
      $[25] = selectedIds;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    let t9;
    if ($[27] !== props) {
      t9 = tag_2 => props.awardTag?.(tag_2) ?? tag_2;
      $[27] = props;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    searchTags = (searchData?.factiiis || []).filter(t8).map(_temp4).map(t9);
    t6 = "flex flex-col";
    if ($[29] !== factiiiQuery) {
      t7 = <Input placeholder="Search for factiiis..." value={factiiiQuery} onChange={setFactiiiQuery} />;
      $[29] = factiiiQuery;
      $[30] = t7;
    } else {
      t7 = $[30];
    }
    t4 = "mt-2 space-y-2";
    t5 = searchTags.length > 0 && <>{searchTags.map(tag_3 => <FactiiiSearchResult key={tag_3.factiiiId} tag={tag_3} replyConfig={tag_3.replyConfig} handleClick={() => props.handleClick(tag_3)} />)}</>;
    $[15] = factiiiQuery;
    $[16] = props;
    $[17] = searchData?.factiiis;
    $[18] = selectedIds;
    $[19] = searchTags;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    searchTags = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
  }
  let t8;
  if ($[31] !== factiiiQuery.length || $[32] !== props.myPostFactiiiCounts.length || $[33] !== searchTags) {
    t8 = factiiiQuery.length > 0 && searchTags.length === 0 && props.myPostFactiiiCounts.length === 0 && <div className="py-4"><p className="mb-2 text-center text-sm text-textSecondary">No factiiis found, click below to create a new one...</p><Button onClick={() => setAddFactiiiModalOpen(true)} text="Create New Factiii" icon={<Plus />} classNames="w-full" /></div>;
    $[31] = factiiiQuery.length;
    $[32] = props.myPostFactiiiCounts.length;
    $[33] = searchTags;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t4 || $[36] !== t5 || $[37] !== t8) {
    t9 = <div className={t4}>{t5}{t8}</div>;
    $[35] = t4;
    $[36] = t5;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== addFactiiiModalOpen || $[40] !== currentUser || $[41] !== factiiiQuery || $[42] !== refetch) {
    t10 = addFactiiiModalOpen && <AddFactiiiModal closeHandler={() => setAddFactiiiModalOpen(false)} type="USER" slug={currentUser.username} refetch={refetch} searchedName={factiiiQuery} />;
    $[39] = addFactiiiModalOpen;
    $[40] = currentUser;
    $[41] = factiiiQuery;
    $[42] = refetch;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== t10 || $[45] !== t6 || $[46] !== t7 || $[47] !== t9) {
    t11 = <div className={t6}>{t7}{t9}{t10}</div>;
    $[44] = t10;
    $[45] = t6;
    $[46] = t7;
    $[47] = t9;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  return t11;
};
function _temp(tag) {
  return tag.factiiiId;
}
function _temp2(previousData) {
  return previousData;
}
function _temp3(t) {
  return t.factiiiId;
}
function _temp4(tag_1) {
  return convertToFactiiiTag(tag_1);
}